import apiClient from './config';
import { Evento, } from "../../types/eventos"
import {AsignarClientesResponse,  ProgressData} from "../../types/eventos"


// Interfaz para la respuesta del servidor
interface CreateMultipleEventosResponse {
    success: boolean;
    message: string;
    count: number;
    /** Clases omitidas por ya existir (mismo nivel, instructor, día y hora). */
    skipped?: number;
}

// Interfaz para los parámetros de eliminación masiva
interface DeleteMultipleEventosParams {
    fechaInicio: string;
    fechaFin: string;
    instructor_id?: string;
    nivel_id?: string;
    sala?: string;
}

// Interfaz para la respuesta de eliminación masiva
interface DeleteMultipleEventosResponse {
    success: boolean;
    message: string;
    deletedCount: number;
    rangoFechas: {
        fechaInicio: string;
        fechaFin: string;
    };
    filtrosAplicados?: {
        instructor_id?: string;
        nivel_id?: string;
        sala?: string;
    };
}

// Servicios para obtener, crear, actualizar y eliminar eventos
export const fetchEventos = async (): Promise<Evento[]> => {
    const response = await apiClient.get('/eventos');
    return response.data;
};

// Función para obtener eventos por rango de fechas
// se transforma el formato de las fechas para que sea compatible con FullCalendar
export const fetchEventosByDateRange = async (start: string, end: string): Promise<Evento[]> => {
    const startDate = new Date(start).toISOString().split('T')[0];
    const endDate = new Date(end).toISOString().split('T')[0];
    // console.log('startDate', startDate);
    // console.log('endDate', endDate);
    const response = await apiClient.get(`/eventos/by-date-range?start=${startDate}&end=${endDate}`);
    // console.log('response', response.data); 
    return response.data.map((evento: Evento) => ({
        id: evento._id,
        title: `${evento.nivel?.nombre ?? 'Nivel eliminado'} (${evento.instructor?.nombre ?? 'instructor eliminado'})`,
        start: `${evento.fecha.split('T')[0]}T${evento.horaInicio}:00`,
        end: `${evento.fecha.split('T')[0]}T${evento.horaFin}:00`,
        ...evento
    }));
};

// Nueva función para obtener eventos sin transformación (para eliminación masiva)
export const fetchEventosByDateRangeRaw = async (start: string, end: string): Promise<Evento[]> => {
    const response = await apiClient.get(`/eventos/by-date-range?start=${start}&end=${end}`);
    return response.data; // Sin transformación
};

// Funcion para crear eventos llamando al endpoint POST /eventos
export const createEvento = async (evento: Evento) => {
    const response = await apiClient.post('/eventos', evento);
    return response.data;
};



// Función para crear múltiples eventos
export const createMultipleEventos = async (eventos: Evento[]): Promise<CreateMultipleEventosResponse> => {
    const response = await apiClient.post('/eventos/multiple', eventos);
    return response.data;
};


// Funcion para actualizar eventos llamando al endpoint PUT /eventos/:eventoId
export const updateEvento = async (evento: Evento): Promise<Evento> => {
    try{
        // Verificamos si debemos actualizar los eventos futuros
        const endpoint = evento.actualizarFuturos 
        ? `/eventos/${evento._id}/actualizar-futuros`
        : `/eventos/${evento._id}`;
            
        const response = await apiClient.put(endpoint, evento);
        return response.data;
    } catch (error) {
        console.error('Error al actualizar el evento:', error);
        throw error;
    }
    
};


// Funcion para eliminar eventos llamando al endpoint DELETE /eventos/:eventoId
export const deleteEvento = async (eventoId: string): Promise<void> => {
    await apiClient.delete(`/eventos/${eventoId}`);
};


// Respuesta al cancelar una clase completa (compensa a los inscritos con recuperaciones)
export interface CancelClassResponse {
    message: string;
    evento: Evento;
    enrolledCount: number;
    recoveriesIssued: number;
}

// Cancelar una clase completa (instructor ausente, etc.) y compensar a los inscritos.
// `compensate` y `notify` son vías de escape para una limpieza —una clase que
// cayó en festivo y no debería existir—: se omiten en el caso normal y el
// backend compensa y avisa salvo que lleguen en `false` explícito.
export const cancelarClaseCompleta = async (
    eventoId: string,
    reason?: string,
    options?: { compensate?: boolean; notify?: boolean }
): Promise<CancelClassResponse> => {
    const response = await apiClient.post(`/eventos/${eventoId}/cancelar-clase`, {
        reason,
        ...options,
    });
    return response.data;
};

// Reestablecer una clase cancelada (revoca las recuperaciones pendientes emitidas por el admin)
export const reestablecerClaseCompleta = async (
    eventoId: string
): Promise<{ message: string; evento: Evento; recoveriesRevoked: number }> => {
    const response = await apiClient.post(`/eventos/${eventoId}/reestablecer-clase`, {});
    return response.data;
};


// Función para eliminar múltiples eventos
export const deleteMultipleEventos = async (params: DeleteMultipleEventosParams): Promise<DeleteMultipleEventosResponse> => {
    const response = await apiClient.delete('/eventos/multiple', { data: params });
    return response.data;
};


export const fetchEventosByDateRangeAndNivel = async (
    startDate: string,
    endDate: string,
    nivelId: string
): Promise<Evento[]> => {
    const response = await apiClient.get('/eventos/by-date-and-nivel', {
        params: { startDate, endDate, nivelId }
    });
    return response.data;
};


// Función para asignar clientes a eventos
export const asignarClientesMasivos = async (fechaInicio: string, fechaFin: string): Promise<AsignarClientesResponse> => {
    const response = await apiClient.post('/eventos/asignar-clientes-masivos', {
        fechaInicio,
        fechaFin
    });
    return response.data;
};

// Función para obtener el progreso de la asignación de clientes
export const fetchAsignacionProgreso = async (sessionId: string): Promise<ProgressData> => {
    const response = await apiClient.get(`/eventos/progreso-asignacion/${sessionId}`);
    return response.data;
};
// D1 — quién está esperando una clase, en orden de turno. Solo lectura.
export interface EventWaitlistEntry {
    _id: string;
    createdAt: string;
    status: 'waiting' | 'claiming';
    clientId: { _id: string; nombre: string; email?: string; telefono?: string; activo?: boolean } | null;
}

export const fetchEventoWaitlist = async (eventoId: string): Promise<EventWaitlistEntry[]> => {
    const response = await apiClient.get(`/eventos/${eventoId}/waitlist`);
    return response.data;
};

// ── P6 — pasar lista ─────────────────────────────────────────────────────────

export type AttendanceStatus = 'present' | 'absent';

export interface RegisterAttendee {
    kind: 'client' | 'prospect';
    id: string;
    name: string;
    /**
     * Su plaza está en pausa SOBRE ESTA CLASE. Una pausa de otras fechas no se
     * enseña: la lista es de una clase concreta y avisar de otra cosa se leería
     * como "no marques a esta persona" un día en el que sí tocaba.
     */
    paused: boolean;
    motivo: string | null;
    /** `null` = nadie ha marcado a esta persona todavía. */
    status: AttendanceStatus | null;
    markedBy: string | null;
    markedAt: string | null;
}

export interface RegisterCancellation {
    clientId: string;
    name: string;
    /** `undefined` en filas anteriores a B9: desconocido, no "sin recuperación". */
    recoveryGranted?: boolean;
    cancelledAt: string;
}

export interface ClassRegister {
    eventId: string;
    cancelled: boolean;
    /**
     * Si alguien ha pasado lista. NO se deduce por asistente: una clase sin
     * ninguna marca significa "no se pasó lista", nunca "no vino nadie".
     */
    taken: boolean;
    window: { state: 'early' | 'open' | 'closed'; startsAt: string; closesAt: string };
    attendees: RegisterAttendee[];
    cancellations: RegisterCancellation[];
}

export const fetchClassRegister = async (eventoId: string): Promise<ClassRegister> => {
    const response = await apiClient.get(`/eventos/${eventoId}/attendance`);
    return response.data;
};

export const markClassRegister = async (
    eventoId: string,
    marks: { kind: 'client' | 'prospect'; id: string; status: AttendanceStatus }[]
): Promise<{ message: string; marked: number; inserted: number; updated: number }> => {
    const response = await apiClient.post(`/eventos/${eventoId}/attendance`, { marks });
    return response.data;
};

// ── P6 fase 2 — lo que ve un instructor ──────────────────────────────────────

export interface TeachingClass {
    _id: string;
    fecha: string;
    horaInicio: string;
    horaFin: string;
    sala: string;
    nivel: { _id?: string; nombre?: string; color?: string } | null;
    /** Solo el recuento: quién está en la clase sale al abrir la lista. */
    inscritos: number;
}

/**
 * Sus clases de un día. El backend filtra por el instructor que firma, así que
 * aquí no se manda ningún id: pedirlo desde el cliente sería pedir que te crean.
 * `unlinked` es el caso de un usuario de instructor que todavía no está enlazado a
 * ninguna ficha de este centro.
 */
export const fetchMyClasses = async (
    from?: string,
    to?: string,
): Promise<{ items: TeachingClass[]; unlinked: boolean }> => {
    const response = await apiClient.get('/teaching/my-classes', { params: { from, to } });
    return response.data;
};

export const fetchMyClassRegister = async (eventoId: string): Promise<ClassRegister> => {
    const response = await apiClient.get(`/teaching/classes/${eventoId}/register`);
    return response.data;
};

export const markMyClassRegister = async (
    eventoId: string,
    marks: { kind: 'client' | 'prospect'; id: string; status: AttendanceStatus }[]
): Promise<{ message: string; marked: number; inserted: number; updated: number }> => {
    const response = await apiClient.post(`/teaching/classes/${eventoId}/register`, { marks });
    return response.data;
};
